// src/utils/sgcAnalystPdf.js
//
// PDF export for the Stop / Go / Continue analyst page.
// Uses pdfMake — declarative doc definition, native page breaks, real tables.
//
// Lazy-imports pdfmake + vfs_fonts so the ~300KB only ships on demand,
// not on every analyst page load.

const KA = {
  red:   '#E83530',
  navy:  '#233C5F',
  gold:  '#A29164',
  paper: '#FAF8F4',
  ink:   '#1A1A1A',
  mute:  '#6B6B6B',
  line:  '#E5E1D8',
};

const COLUMN_COLORS = {
  stop:     KA.red,
  go:       '#2E7D32',
  continue: KA.navy,
};

// Build the pdfMake document definition from a synthesis doc.
//
// `meta` carries display metadata for the cover area:
//   { title: 'Whole Session' | 'Dining' | 'STOP · all topics', inputCount, generatedAt: Date }
function buildDocDef(synthesis, meta) {
  const s = synthesis || {};
  const generatedStr = meta.generatedAt
    ? meta.generatedAt.toLocaleString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      })
    : '—';

  const content = [];

  // ── Title block ────────────────────────────────────────────────────
  content.push(
    { text: "King's Academy · Madaba, Jordan", style: 'eyebrow' },
    { text: 'Stop / Go / Continue', style: 'h1' },
    { text: meta.title, style: 'h2' },
    {
      text: `Synthesis generated ${generatedStr} · based on ${meta.inputCount} ${meta.inputCount === 1 ? 'submission' : 'submissions'}`,
      style: 'subhead',
      margin: [0, 0, 0, 18],
    }
  );

  // ── Headline ──────────────────────────────────────────────────────
  if (s.headline) {
    content.push({
      table: {
        widths: ['*'],
        body: [[{
          text: s.headline,
          style: 'headline',
          fillColor: '#FFF8EC',
          border: [false, false, false, false],
          margin: [12, 10, 12, 10],
        }]],
      },
      layout: {
        defaultBorder: false,
      },
      margin: [0, 0, 0, 18],
    });
  }

  // ── Themes ────────────────────────────────────────────────────────
  content.push({ text: 'Themes & frequency', style: 'sectionHead' });
  content.push({
    text: 'Patterns that came up across multiple groups.',
    style: 'sectionSub',
    margin: [0, 0, 0, 8],
  });
  const themes = Array.isArray(s.themes) ? s.themes : [];
  if (themes.length === 0) {
    content.push({ text: 'No themes surfaced for this slice.', style: 'empty', margin: [0, 0, 0, 14] });
  } else {
    themes.forEach((t) => {
      content.push(buildThemeBlock(t));
    });
  }

  // ── Action orientation ────────────────────────────────────────────
  content.push({ text: 'Action orientation', style: 'sectionHead', pageBreak: undefined });
  content.push({
    text: 'Concrete actions vs. abstract values.',
    style: 'sectionSub',
    margin: [0, 0, 0, 8],
  });
  if (s.actionOrientation?.note) {
    content.push({
      text: s.actionOrientation.note,
      style: 'note',
      margin: [0, 0, 0, 10],
    });
  }
  content.push({
    columns: [
      buildAOColumn('Concrete', 'Specific, doable changes', s.actionOrientation?.concrete),
      buildAOColumn('Abstract', 'Values, principles, aspirations', s.actionOrientation?.abstract),
    ],
    columnGap: 12,
    margin: [0, 0, 0, 14],
  });

  // ── Tensions ──────────────────────────────────────────────────────
  content.push({ text: 'Where groups disagreed', style: 'sectionHead' });
  content.push({
    text: 'Tensions to be aware of.',
    style: 'sectionSub',
    margin: [0, 0, 0, 8],
  });
  const tensions = Array.isArray(s.tensions) ? s.tensions : [];
  if (tensions.length === 0) {
    content.push({ text: 'No clear tensions surfaced in this slice.', style: 'empty', margin: [0, 0, 0, 14] });
  } else {
    tensions.forEach((t) => {
      content.push(buildTensionBlock(t));
    });
  }

  // ── Quick wins vs structural ──────────────────────────────────────
  content.push({ text: 'Quick wins vs. structural changes', style: 'sectionHead' });
  content.push({
    text: "What's actionable now vs. what requires bigger shifts.",
    style: 'sectionSub',
    margin: [0, 0, 0, 8],
  });
  content.push({
    columns: [
      buildAOColumn('Quick wins', 'Doable in weeks, low cost', s.quickWinsVsStructural?.quickWins, '#FFF8EC'),
      buildAOColumn('Structural', 'Months/years, policy/budget', s.quickWinsVsStructural?.structural),
    ],
    columnGap: 12,
    margin: [0, 0, 0, 14],
  });

  // ── Surfaced from brainstorm ──────────────────────────────────────
  content.push({ text: 'Surfaced from brainstorm', style: 'sectionHead' });
  content.push({
    text: "Items raised in brainstorming but not elevated to any group's top 5 — worth a second look.",
    style: 'sectionSub',
    margin: [0, 0, 0, 8],
  });
  const surfaced = Array.isArray(s.surfacedFromBrainstorm) ? s.surfacedFromBrainstorm : [];
  if (surfaced.length === 0) {
    content.push({ text: 'Nothing notable surfaced beneath the top picks.', style: 'empty' });
  } else {
    surfaced.forEach((sf) => {
      content.push(buildSurfacedBlock(sf));
    });
  }

  return {
    info: {
      title: `SGC Synthesis — ${meta.title}`,
      author: "King's Academy Faculty Tool Kit",
      subject: 'Stop / Go / Continue PD Synthesis',
    },
    pageSize: 'LETTER',
    pageMargins: [50, 56, 50, 56],
    footer: (currentPage, pageCount) => ({
      columns: [
        { text: 'Stop / Go / Continue · Faculty PD', style: 'footer', alignment: 'left' },
        { text: `${currentPage} of ${pageCount}`, style: 'footer', alignment: 'right' },
      ],
      margin: [50, 0, 50, 20],
    }),
    content,
    styles: {
      eyebrow: {
        fontSize: 9,
        color: KA.gold,
        bold: true,
        characterSpacing: 1.2,
      },
      h1: {
        fontSize: 22,
        bold: true,
        color: KA.navy,
        margin: [0, 4, 0, 2],
      },
      h2: {
        fontSize: 16,
        color: KA.navy,
        margin: [0, 0, 0, 2],
      },
      subhead: {
        fontSize: 10,
        color: KA.mute,
      },
      headline: {
        fontSize: 12,
        color: KA.ink,
        italics: false,
      },
      sectionHead: {
        fontSize: 14,
        bold: true,
        color: KA.navy,
        margin: [0, 10, 0, 2],
      },
      sectionSub: {
        fontSize: 10,
        color: KA.mute,
        italics: true,
      },
      themeLabel: {
        fontSize: 12,
        bold: true,
        color: KA.navy,
      },
      themeCount: {
        fontSize: 9,
        bold: true,
        color: 'white',
      },
      themeSummary: {
        fontSize: 10,
        color: KA.ink,
        lineHeight: 1.35,
      },
      themeDistPill: {
        fontSize: 8,
        bold: true,
        color: 'white',
      },
      bullet: {
        fontSize: 9,
        color: KA.ink,
        lineHeight: 1.35,
      },
      note: {
        fontSize: 10,
        color: KA.ink,
        italics: true,
        lineHeight: 1.4,
      },
      empty: {
        fontSize: 10,
        color: KA.mute,
        italics: true,
      },
      aoHead: {
        fontSize: 12,
        bold: true,
        color: KA.navy,
      },
      aoBlurb: {
        fontSize: 9,
        color: KA.mute,
        italics: true,
      },
      aoItemLabel: {
        fontSize: 10,
        bold: true,
        color: KA.ink,
      },
      aoItemSummary: {
        fontSize: 9,
        color: KA.mute,
        lineHeight: 1.35,
      },
      tensionLabel: {
        fontSize: 12,
        bold: true,
        color: KA.navy,
      },
      tensionSideLabel: {
        fontSize: 8,
        bold: true,
        color: KA.gold,
        characterSpacing: 1,
      },
      tensionSideSummary: {
        fontSize: 10,
        color: KA.ink,
        lineHeight: 1.35,
      },
      footer: {
        fontSize: 9,
        color: KA.mute,
      },
    },
    defaultStyle: {
      // pdfMake's built-in default is Roboto (bundled in vfs_fonts).
      // Helvetica/Georgia aren't bundled by default; using Roboto avoids
      // a custom font registration step.
      font: 'Roboto',
      fontSize: 10,
      color: KA.ink,
    },
  };
}

// Theme card as a single-cell table with a colored header strip.
function buildThemeBlock(theme) {
  const examples = Array.isArray(theme.examples) ? theme.examples : [];
  const dist = theme.columnDistribution || {};
  const distPills = ['stop', 'go', 'continue']
    .filter((cid) => (dist[cid] || 0) > 0)
    .map((cid) => ({
      text: `${cid.toUpperCase()} · ${dist[cid]}`,
      style: 'themeDistPill',
      fillColor: COLUMN_COLORS[cid],
      margin: [6, 1, 6, 1],
    }));

  const body = [
    [
      { text: theme.label || '(theme)', style: 'themeLabel', border: [false, false, false, false] },
      {
        text: typeof theme.groupCount === 'number'
          ? `${theme.groupCount} ${theme.groupCount === 1 ? 'group' : 'groups'}`
          : '',
        style: 'themeCount',
        fillColor: KA.navy,
        alignment: 'center',
        margin: [6, 2, 6, 2],
        border: [false, false, false, false],
      },
    ],
    [
      {
        colSpan: 2,
        text: theme.summary || '',
        style: 'themeSummary',
        border: [false, false, false, false],
        margin: [0, 4, 0, 4],
      },
      {},
    ],
  ];

  if (distPills.length > 0) {
    body.push([
      {
        colSpan: 2,
        columns: distPills.map((p) => ({ text: p.text, style: p.style, fillColor: p.fillColor, alignment: 'center' })),
        columnGap: 4,
        border: [false, false, false, false],
        margin: [0, 0, 0, 4],
      },
      {},
    ]);
  }

  if (examples.length > 0) {
    body.push([
      {
        colSpan: 2,
        ul: examples.map((ex) => ({ text: ex, style: 'bullet' })),
        border: [false, false, false, false],
      },
      {},
    ]);
  }

  return {
    table: {
      widths: ['*', 'auto'],
      body,
    },
    layout: {
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      paddingLeft:   () => 10,
      paddingRight:  () => 10,
      paddingTop:    () => 8,
      paddingBottom: () => 8,
      fillColor: (rowIdx) => rowIdx === 0 ? '#FDFCFA' : '#FDFCFA',
    },
    margin: [0, 0, 0, 8],
    unbreakable: true,
  };
}

// Action-orientation or quick-wins column.
function buildAOColumn(heading, blurb, items, tint) {
  const list = Array.isArray(items) ? items : [];
  const stack = [
    { text: heading, style: 'aoHead' },
    { text: blurb, style: 'aoBlurb', margin: [0, 0, 0, 6] },
  ];
  if (list.length === 0) {
    stack.push({ text: '(none in this slice)', style: 'empty' });
  } else {
    list.forEach((it, i) => {
      stack.push({ text: it.label || '(item)', style: 'aoItemLabel', margin: [0, i === 0 ? 0 : 4, 0, 1] });
      if (it.summary) {
        stack.push({ text: it.summary, style: 'aoItemSummary' });
      }
      if (Array.isArray(it.examples) && it.examples.length > 0) {
        stack.push({
          ul: it.examples.map((ex) => ({ text: ex, style: 'bullet' })),
          margin: [0, 2, 0, 0],
        });
      }
    });
  }

  return {
    table: {
      widths: ['*'],
      body: [[{ stack, border: [false, false, false, false], fillColor: tint || null, margin: [10, 10, 10, 10] }]],
    },
    layout: { defaultBorder: false },
  };
}

// Tension block — two side-by-side cells with optional leadership note.
function buildTensionBlock(tension) {
  const sideA = tension.sideA || {};
  const sideB = tension.sideB || {};

  const buildSide = (label, side) => {
    const stack = [
      { text: label, style: 'tensionSideLabel' },
      { text: side.summary || '', style: 'tensionSideSummary', margin: [0, 2, 0, 0] },
    ];
    if (Array.isArray(side.examples) && side.examples.length > 0) {
      stack.push({
        ul: side.examples.map((ex) => ({ text: ex, style: 'bullet' })),
        margin: [0, 4, 0, 0],
      });
    }
    return { stack, border: [false, false, false, false], margin: [10, 8, 10, 8] };
  };

  const rows = [
    [{
      colSpan: 2,
      text: tension.label || '(tension)',
      style: 'tensionLabel',
      border: [false, false, false, false],
      margin: [0, 0, 0, 4],
    }, {}],
    [
      { ...buildSide('Side A', sideA), fillColor: 'white' },
      { ...buildSide('Side B', sideB), fillColor: 'white' },
    ],
  ];

  if (tension.note) {
    rows.push([
      {
        colSpan: 2,
        text: tension.note,
        fillColor: '#FFF8EC',
        margin: [10, 8, 10, 8],
        border: [false, false, false, false],
        fontSize: 10,
        color: KA.ink,
        italics: true,
      },
      {},
    ]);
  }

  return {
    table: {
      widths: ['*', '*'],
      body: rows,
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => KA.line,
      vLineColor: () => KA.line,
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 0,
      paddingBottom: () => 0,
    },
    margin: [0, 0, 0, 10],
    unbreakable: true,
  };
}

// Surfaced-from-brainstorm card — slim, gold-tinted left edge.
function buildSurfacedBlock(s) {
  const examples = Array.isArray(s.examples) ? s.examples : [];
  const stack = [
    { text: s.label || '(item)', style: 'themeLabel' },
  ];
  if (s.summary) {
    stack.push({ text: s.summary, style: 'themeSummary', margin: [0, 2, 0, 0] });
  }
  if (examples.length > 0) {
    stack.push({
      ul: examples.map((ex) => ({ text: ex, style: 'bullet' })),
      margin: [0, 2, 0, 0],
    });
  }
  return {
    table: {
      widths: [3, '*'],
      body: [[
        { text: '', fillColor: KA.gold, border: [false, false, false, false] },
        { stack, fillColor: '#FDFCFA', border: [false, false, false, false], margin: [10, 8, 10, 8] },
      ]],
    },
    layout: { defaultBorder: false },
    margin: [0, 0, 0, 8],
    unbreakable: true,
  };
}

// Sanitize the filename — strip slashes, periods, etc.
function sanitizeFilename(s) {
  return String(s)
    .replace(/[\/\\:*?"<>|]/g, '-')
    .replace(/\s+/g, '_')
    .slice(0, 80);
}

// ── Public entry point ──────────────────────────────────────────────
// Loads pdfmake lazily, then generates and triggers a download.
//
// Throws on failure — caller should try/catch.
export async function downloadSynthesisPdf({ synthesis, meta }) {
  // Lazy-import so initial bundle stays slim.
  const pdfMakeModule = await import('pdfmake/build/pdfmake');
  const vfsFontsModule = await import('pdfmake/build/vfs_fonts');

  // pdfmake's UMD build sometimes resolves the namespace via `.default`
  // depending on bundler config. Handle both shapes.
  const pdfMake = pdfMakeModule.default || pdfMakeModule;
  const vfsFonts = vfsFontsModule.default || vfsFontsModule;

  // Wire up the bundled Roboto font.
  // Older vfs_fonts puts the table at .pdfMake.vfs; newer versions put it
  // at the module root as .vfs. Try both.
  pdfMake.vfs = vfsFonts.pdfMake?.vfs || vfsFonts.vfs || vfsFonts;

  const docDef = buildDocDef(synthesis, meta);
  const filename = `SGC_${sanitizeFilename(meta.title)}_${new Date().toISOString().slice(0, 10)}.pdf`;
  pdfMake.createPdf(docDef).download(filename);
}